import { useEffect, useRef } from 'react';

/**
 * 滚动触底加载更多：禁止首屏 sentinel 可见或 Observer 重绑时连环翻页。
 *
 * 规则：
 * 1. sentinel 至少「离开过视口」一次后才允许加载（首屏贴底不自动拉）
 * 2. 每次成功触发后解除武装；若 loading 结束后仍贴底，则重新武装并允许继续翻页
 *    （修复：加载后 sentinel 仍相交时 IO 不再回调、用户向下滑却拉不出下一页）
 * 3. 用 ref 读取 hasMore/loadingMore/onLoadMore，避免依赖变化导致 Observer 重绑连环请求
 */
export function useArmedScrollLoadMore({
  enabled = true,
  hasMore = false,
  loadingMore = false,
  onLoadMore,
  sentinelRef,
  scrollRoot = null,
  rootMargin = '320px',
  bottomThresholdPx = 420,
}) {
  const hasMoreRef = useRef(hasMore);
  const loadingMoreRef = useRef(loadingMore);
  const onLoadMoreRef = useRef(onLoadMore);
  const armedRef = useRef(false);
  const prevLoadingMoreRef = useRef(loadingMore);
  const scrollRootRef = useRef(scrollRoot);
  const bottomThresholdRef = useRef(bottomThresholdPx);

  hasMoreRef.current = hasMore;
  loadingMoreRef.current = loadingMore;
  onLoadMoreRef.current = onLoadMore;
  scrollRootRef.current = scrollRoot;
  bottomThresholdRef.current = bottomThresholdPx;

  const isNearBottom = () => {
    const root = scrollRootRef.current;
    if (!root) return false;
    const remaining = root.scrollHeight - root.scrollTop - root.clientHeight;
    return remaining <= bottomThresholdRef.current;
  };

  const requestLoadMore = () => {
    if (!armedRef.current) return;
    if (!hasMoreRef.current || loadingMoreRef.current) return;
    if (typeof onLoadMoreRef.current !== 'function') return;
    armedRef.current = false;
    onLoadMoreRef.current();
  };

  useEffect(() => {
    if (!enabled || typeof onLoadMore !== 'function') {
      armedRef.current = false;
      return undefined;
    }

    const node = sentinelRef?.current;
    const root = scrollRoot || null;

    const tryLoadMoreFromScroll = () => {
      if (!root) return;
      if (!isNearBottom()) {
        armedRef.current = true;
        return;
      }
      requestLoadMore();
    };

    let observer = null;
    if (node && typeof IntersectionObserver !== 'undefined') {
      observer = new IntersectionObserver(
        (entries) => {
          const hitting = entries.some((entry) => entry.isIntersecting);
          if (!hitting) {
            armedRef.current = true;
            return;
          }
          requestLoadMore();
        },
        { root, rootMargin },
      );
      observer.observe(node);
    }

    if (root) {
      root.addEventListener('scroll', tryLoadMoreFromScroll, { passive: true });
    }

    return () => {
      observer?.disconnect();
      root?.removeEventListener('scroll', tryLoadMoreFromScroll);
    };
    // 故意不依赖 hasMore / loadingMore / onLoadMore，避免重绑连环拉取
  }, [enabled, sentinelRef, scrollRoot, rootMargin, onLoadMore]);

  // loading 结束后若仍贴底：重新武装并继续翻页（IO 在持续相交时不会再次回调）
  useEffect(() => {
    const wasLoading = prevLoadingMoreRef.current;
    prevLoadingMoreRef.current = loadingMore;
    if (!enabled || !wasLoading || loadingMore) return undefined;
    if (!hasMoreRef.current) return undefined;

    let raf = 0;
    let cancelled = false;
    raf = requestAnimationFrame(() => {
      if (cancelled) return;
      if (!hasMoreRef.current || loadingMoreRef.current) return;
      if (!isNearBottom()) return;
      armedRef.current = true;
      requestLoadMore();
    });

    return () => {
      cancelled = true;
      if (raf) cancelAnimationFrame(raf);
    };
  }, [loadingMore, enabled]);
}
